import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Divider,
  Button,
  Typography,
  Switch,
  CircularProgress,
  TextField,
  MenuItem,
} from "@mui/material";
import { Upload as UploadIcon } from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import ThemeFieldInput from "./ThemeFieldInput";
import SubLayout from "./SubLayout";
import { customizationApi } from "../../services/customization-service";
import { organizationApi } from "../../services/organization-service";
import {
  defaultStoredTheme,
  setStoredTheme,
  useStoredTheme,
} from "@/utils/theme-context";
import {
  needsPlatformTabBranding,
  resolveTabBranding,
} from "@/utils/theme-branding";
import { useTranslation } from "react-i18next";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useSupportedLanguages } from "../../hooks/useSupportedLanguages";

const hiddenThemeFields = {
  playground: new Set(["accentGreen", "iconFill", "menuBorder"]),
};

const themeAssetTypes = ["logo", "wideLogo", "favicon"];
const themeModes = ["light", "dark"];
// Temporarily hidden until the updated tab-branding wireframe is available.
const showTabBrandingFields = false;
const createEmptyThemeAssetFiles = () =>
  Object.fromEntries(
    themeAssetTypes.map((type) => [type, { light: null, dark: null }]),
  );

const MaterialUISwitch = styled(Switch)(({ theme }) => {
  const storedTheme = useStoredTheme();
  const bg = storedTheme.inputSwitch.backgroundColor[theme.palette.mode];
  const isRTL = theme.direction === "rtl";

  return {
    width: 62,
    height: 34,
    padding: 7,
    "& .MuiSwitch-switchBase": {
      margin: 1,
      padding: 0,
      transform: isRTL
        ? "translateX(-6px) !important"
        : "translateX(6px) !important",
      "&.Mui-checked": {
        color: "var(--color-3)",
        transform: isRTL
          ? "translateX(-22px) !important"
          : "translateX(22px) !important",
        "& .MuiSwitch-thumb:before": {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            "var(--color-3)",
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        "& + .MuiSwitch-track": {
          backgroundColor: bg,
          opacity: 0.5,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      backgroundColor: bg,
      width: 32,
      height: 32,
      "&:before": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        insetInlineStart: 0,
        top: 0,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "var(--color-3)",
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    "& .MuiSwitch-track": {
      opacity: 0.3,
      borderRadius: 20 / 2,
    },
  };
});

const ThemeComp = ({}) => {
  const { t } = useTranslation();
  const {
    languages: supportedLanguages,
    isLoading: isLoadingSupportedLanguages,
  } = useSupportedLanguages();
  const { updateOrganizationLanguage } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [mode, setMode] = useState("dark");
  const userTheme = useTheme();
  const storedTheme = useStoredTheme();
  const { organization_id } = useParams();
  const [isDefault, setIsDefault] = useState(false);
  const hasRun = useRef(false);
  const [defaultTheme, setDefalutTheme] = useState(defaultStoredTheme);
  const [theme, setTheme] = useState(structuredClone(defaultTheme));
  const [orgRole, setOrgRole] = useState(null);
  const [loadedThemeOrganizationId, setLoadedThemeOrganizationId] =
    useState(null);
  const [themeAssetFiles, setThemeAssetFiles] = useState(
    createEmptyThemeAssetFiles,
  );
  const [initialLanguage, setInitialLanguage] = useState(
    defaultStoredTheme.language,
  );

  useEffect(() => {
    hasRun.current = false;
    getOrganizations();
  }, [organization_id]);

  const getOrganizations = async () => {
    if (!organization_id) return;

    try {
      setLoading(true);
      const response = await organizationApi.getOrganizations();
      setOrgRole(
        response.data.find((each) => each.id === organization_id)?.role_id,
      );
    } catch (err) {
      console.error("Error loading organizations:", err);
      toast.error(t("theme.failedToLoadOrganizations"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasRun.current && orgRole === "owner") {
      hasRun.current = true;
      getTheme();
    }
  }, [orgRole, organization_id]);

  const remToPx = (val, base = 16) => {
    if (typeof val !== "string") return val;

    if (val.includes("rem")) {
      const num = parseFloat(val.replace("rem", ""));
      return `${num * base}px`;
    }

    return val;
  };

  const convertRadiusToPx = (obj) => {
    if (typeof obj !== "object" || obj === null) return obj;

    const newObj = Array.isArray(obj) ? [] : {};

    for (const key in obj) {
      const value = obj[key];

      if (key.toLowerCase().includes("borderradius")) {
        newObj[key] = {
          light: remToPx(value.light),
          dark: remToPx(value.dark),
        };
      } else if (typeof value === "object") {
        newObj[key] = convertRadiusToPx(value);
      } else {
        newObj[key] = value;
      }
    }

    return newObj;
  };

  const getOrganizationLanguage = async () => {
    const { data } = await organizationApi.getOrganization(organization_id);

    return (
      data?.default_language || data?.language || defaultStoredTheme.language
    );
  };

  const setActiveOrganizationRuntimeTheme = async (organizationTheme) => {
    if (localStorage.getItem("currentOrganizationId") !== organization_id) {
      return;
    }

    let runtimeTheme = organizationTheme;

    if (needsPlatformTabBranding(runtimeTheme)) {
      let platformTheme = storedTheme;

      try {
        const { data } = await customizationApi.getDefaultTheme();
        platformTheme = data?.theme || storedTheme;
      } catch {
        // Preserve the already loaded platform branding if a refresh fails.
      }

      runtimeTheme = resolveTabBranding(
        runtimeTheme,
        platformTheme,
        defaultStoredTheme,
      );
    }

    setStoredTheme(runtimeTheme);
  };

  const getTheme = async () => {
    if (!organization_id) return;

    setLoading(true);
    let organizationLanguage = defaultStoredTheme.language;

    try {
      organizationLanguage = await getOrganizationLanguage();
      const { data } = await customizationApi.getTheme(organization_id);
      setIsDefault(data?.is_default || false);
      if (data?.theme) {
        const updatedTheme = convertRadiusToPx({
          ...data.theme,
          favicon: {
            ...defaultStoredTheme.favicon,
            ...(data.theme.favicon || {}),
          },
          tabName: data.theme.tabName || "",
        });
        updatedTheme.language = organizationLanguage;
        setDefalutTheme(structuredClone(updatedTheme));
        setTheme(structuredClone(updatedTheme));
        setInitialLanguage(organizationLanguage);
        await setActiveOrganizationRuntimeTheme(updatedTheme);
      }
    } catch (err) {
      // 404 means no custom theme exists yet — treat as default
      if (err?.response?.status === 404) {
        const defaultThemeWithLanguage = {
          ...structuredClone(defaultStoredTheme),
          language: organizationLanguage,
        };

        setIsDefault(true);
        setDefalutTheme(structuredClone(defaultThemeWithLanguage));
        setTheme(structuredClone(defaultThemeWithLanguage));
        setInitialLanguage(organizationLanguage);
        await setActiveOrganizationRuntimeTheme(defaultThemeWithLanguage);
      } else {
        toast.error(
          err?.response?.data?.detail ||
            err.message ||
            t("theme.failedToLoadTheme"),
        );
      }
    } finally {
      setLoadedThemeOrganizationId(organization_id);
      setLoading(false);
    }
  };

  const toggleTheme = () => {
    setMode((prev) => (prev === "light" ? "dark" : "light"));
  };

  const updateThemeField = (path, value) => {
    setTheme((prev) => {
      const copy = structuredClone(prev);
      let ref = copy;
      path.slice(0, -1).forEach((key) => (ref = ref[key]));
      const field = path[path.length - 1];

      if (
        ref[field] &&
        typeof ref[field] === "object" &&
        ("light" in ref[field] || "dark" in ref[field])
      ) {
        ref[field][mode] = value;
      } else {
        ref[field] = value;
      }

      return copy;
    });
  };

  const handleLogoUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (
      !["image/jpg", "image/png", "image/jpeg", "image/svg+xml"].includes(
        file.type,
      )
    ) {
      alert(t("theme.onlyImagesAllowed"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert(t("theme.maxFileSize10mb"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      updateThemeField([type], e.target.result);
    };
    reader.readAsDataURL(file);

    setThemeAssetFiles((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [mode]: file,
      },
    }));
  };

  const isValidHex = (val) =>
    /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(
      val,
    );
  const isValidRadius = (val) => {
    if (val === "") return true;

    const cleaned = String(val).replace("px", "").trim();

    if (isNaN(cleaned)) return false;

    const num = Number(cleaned);

    return num >= 0 && num <= 100;
  };

  const hasInvalidFields = (themeObj, mode = "dark") => {
    for (const key in themeObj) {
      const value = themeObj[key];

      // if (value === null) continue;

      if (
        typeof value === "object" &&
        value !== null &&
        ("light" in value || "dark" in value)
      ) {
        const modeValue = value[mode];

        // if (modeValue === "" || modeValue == null) continue;

        if (key.toLowerCase().includes("color") && !isValidHex(modeValue))
          return true;

        if (key.toLowerCase().includes("radius") && !isValidRadius(modeValue))
          return true;
      } else if (typeof value === "object") {
        if (hasInvalidFields(value, mode)) return true;
      } else {
        // if (value === "") continue;

        const lowerKey = key.toLowerCase();

        if (lowerKey.includes("color") && !isValidHex(value)) return true;

        if (lowerKey.includes("radius") && !isValidRadius(value)) return true;
      }
    }

    return false;
  };

  const getChangedFields = (original, updated) => {
    const changes = {};

    for (const key in updated) {
      if (
        typeof updated[key] === "object" &&
        updated[key] !== null &&
        !Array.isArray(updated[key])
      ) {
        const nestedChanges = getChangedFields(
          original?.[key] ?? {},
          updated[key],
        );

        if (Object.keys(nestedChanges).length > 0) {
          changes[key] = nestedChanges;
        }
      } else if (updated[key] !== original?.[key]) {
        changes[key] = updated[key];
      }
    }

    return changes;
  };

  const omitLanguage = (themeObj) => {
    const copy = structuredClone(themeObj);
    delete copy.language;

    return copy;
  };

  const getThemeChanges = (themeToCompare = theme) =>
    getChangedFields(omitLanguage(defaultTheme), omitLanguage(themeToCompare));

  const hasThemeChanges = Object.keys(getThemeChanges()).length > 0;
  const hasLanguageChanges = theme.language !== initialLanguage;
  const hasPendingThemeAssets = themeAssetTypes.some((type) =>
    themeModes.some((themeMode) => themeAssetFiles[type][themeMode]),
  );
  const hasChanges =
    hasThemeChanges || hasLanguageChanges || hasPendingThemeAssets;

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasChanges) return;

      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  const resetTheme = async () => {
    setThemeAssetFiles(createEmptyThemeAssetFiles());
    if (!organization_id) return;
    if (isDefault) {
      setTheme((prevTheme) => {
        const newTheme = structuredClone(prevTheme);

        const applyReset = (current, defaults) => {
          Object.keys(defaults).forEach((key) => {
            if (
              typeof defaults[key] === "object" &&
              defaults[key] !== null &&
              !("light" in defaults[key] && "dark" in defaults[key])
            ) {
              applyReset(current[key], defaults[key]);
            } else if (defaults[key]?.[mode]) {
              current[key][mode] = defaults[key][mode];
            } else {
              current[key] = defaults[key];
            }
          });
        };

        applyReset(newTheme, defaultTheme);

        return newTheme;
      });
    } else {
      setActionLoading(true);
      try {
        await customizationApi.deleteTheme(organization_id, mode);
        try {
          await customizationApi.patchTheme(organization_id, {
            updates: {
              tabName: "",
              [`favicon.${mode}`]: "",
            },
          });
        } catch (err) {
          // Deleting the final customized mode removes the entire theme, so
          // there is no remaining tab branding to clear in that case.
          if (err?.response?.status !== 404) throw err;
        }
        await getTheme();
        toast.success(t("theme.resetSuccess"));
      } catch (err) {
        toast.error(
          err?.response?.data?.detail ||
            err.message ||
            t("theme.failedToCreateTheme"),
        );
      } finally {
        setActionLoading(false);
      }
    }
  };

  const saveTheme = async (themeToSave = theme) => {
    if (!organization_id) return;

    setActionLoading(true);
    try {
      const languageToSave = themeToSave.language;
      const languageChanged = languageToSave !== initialLanguage;
      const hasCustomizationChanges =
        hasThemeChanges || hasPendingThemeAssets;
      const customizationThemeToSave = omitLanguage(themeToSave);
      const resolvedThemeToSave = structuredClone(themeToSave);

      if (languageChanged) {
        await customizationApi.updateLanguage(organization_id, languageToSave);
        setInitialLanguage(languageToSave);
        updateOrganizationLanguage(organization_id, languageToSave);
      }

      for (const type of themeAssetTypes) {
        for (const themeMode of themeModes) {
          const file = themeAssetFiles[type][themeMode];
          if (!file) continue;

          const { data } = await customizationApi.uploadThemeLogo(
            organization_id,
            file,
            themeMode,
            type,
          );
          const objectKey = data?.logo?.object_key;

          if (!objectKey) {
            throw new Error(
              t("theme.failedToUploadThemeAsset", { type }),
            );
          }

          customizationThemeToSave[type][themeMode] = objectKey;
          resolvedThemeToSave[type][themeMode] = objectKey;
        }
      }

      if (hasCustomizationChanges) {
        if (isDefault) {
          await customizationApi.createTheme(organization_id, {
            org_id: organization_id,
            theme: customizationThemeToSave,
          });
        } else {
          const changedTheme = getThemeChanges(resolvedThemeToSave);
          if (changedTheme.favicon) {
            Object.entries(changedTheme.favicon).forEach(
              ([themeMode, favicon]) => {
                changedTheme[`favicon.${themeMode}`] = favicon;
              },
            );
            delete changedTheme.favicon;
          }
          await customizationApi.patchTheme(organization_id, {
            updates: changedTheme,
          });
        }
      }

      setThemeAssetFiles(createEmptyThemeAssetFiles());
      await getTheme();
      toast.success(t("theme.saveSuccess"));
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err.message ||
          t("theme.failedToCreateTheme"),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const renderThemeSection = (sectionObj, path, label) => {
    if (!sectionObj) return null;

    const section = path.reduce((acc, key) => acc?.[key], theme);
    if (!section) return null;

    return (
      <>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            mb: 0.5,
            color:
              userTheme.palette.mode === "dark"
                ? "var(--color-31)"
                : "var(--color-32)",
          }}
        >
          {label}
        </Typography>
        {Object.keys(section)
          .filter((field) => !hiddenThemeFields[path.join(".")]?.has(field))
          .map((field, index) => (
            <Box key={index} sx={{ mt: 1 }}>
              <ThemeFieldInput
                disabled={loading}
                label={field}
                value={section[field][mode]}
                onChange={(v) => updateThemeField([...path, field], v)}
              />
            </Box>
          ))}
        <Divider sx={{ my: 1 }} />
      </>
    );
  };

  const boxSx = {
    height: "calc(100vh - 8rem)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.5rem",
    borderRadius: "1rem",
    border: "1px solid",
    borderColor: defaultStoredTheme.card.borderColor[userTheme.palette.mode],
    background:
      defaultStoredTheme.general.backgroundColor[userTheme.palette.mode],
  };

  const isInitialThemeLoading =
    orgRole === "owner" && loadedThemeOrganizationId !== organization_id;

  return loading || isInitialThemeLoading ? (
    <Box sx={boxSx}>
      <CircularProgress size={24} />
    </Box>
  ) : orgRole !== "owner" ? (
    <Box sx={boxSx}>
      You currently do not have permission to change the theme. This option is
      restricted to organization administrators only.
    </Box>
  ) : (
    <Box
      sx={{
        padding: "1.5rem",
        borderRadius: "1rem",
        border: "1px solid",
        borderColor:
          defaultStoredTheme.card.borderColor[userTheme.palette.mode],
        background:
          defaultStoredTheme.general.backgroundColor[userTheme.palette.mode],
      }}
    >
      <Grid container spacing={4} sx={{ mb: 3 }}>
        <Grid item xs={4} md={4}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-4)"
                  : "var(--color-34)",
            }}
          >
            {t("uiCustomization.language")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 400,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-31)"
                  : "var(--color-32)",
            }}
          >
            {t("uiCustomization.selectOrganizationDefaultLanguage")}
          </Typography>
          <TextField
            select
            label={t("uiCustomization.language")}
            name="language"
            value={theme.language}
            onChange={(e) => updateThemeField(["language"], e.target.value)}
            fullWidth
            disabled={actionLoading || isLoadingSupportedLanguages}
            margin="normal"
          >
            {supportedLanguages.map((language) => (
              <MenuItem key={language.code} value={language.code}>
                {t(language.labelKey, { defaultValue: language.name })}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={4} md={4}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-4)"
                  : "var(--color-34)",
            }}
          >
            {t("uiCustomization.logo")}
          </Typography>
          <Typography
            x={{
              fontSize: "0.875rem",
              fontWeight: "400",
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-31)"
                  : "var(--color-32)",
            }}
          >
            {t("uiCustomization.logoDesc")}
          </Typography>
          <Box
            sx={{
              marginTop: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                padding: "0.5rem",
                height: "4rem",
                width: "4rem",
                borderRadius: "0.5rem",
                border: "1px solid",
                borderColor:
                  defaultStoredTheme.card.borderColor[userTheme.palette.mode],
                background:
                  defaultStoredTheme.card.backgroundColor[
                    userTheme.palette.mode
                  ],
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {theme.logo[mode] ? (
                <img
                  src={theme.logo[mode]}
                  alt="logo"
                  crossOrigin="anonymous"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </Box>

            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="logoInput"
              onChange={(e) => handleLogoUpload(e, "logo")}
            />

            <label htmlFor="logoInput">
              <Button
                variant="contained"
                sx={{ borderRadius: "10rem" }}
                component="span"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <UploadIcon />
                    <Typography sx={{ marginInlineStart: "0.5rem" }}>
                      {t("uiCustomization.upload")}
                    </Typography>
                  </>
                )}
              </Button>
            </label>
          </Box>
        </Grid>
        <Grid item xs={4} md={4}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-4)"
                  : "var(--color-34)",
            }}
          >
            {t("uiCustomization.wideLogo")}
          </Typography>
          <Typography
            x={{
              fontSize: "0.875rem",
              fontWeight: "400",
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-31)"
                  : "var(--color-32)",
            }}
          >
            {t("uiCustomization.wideLogoDesc")}
          </Typography>
          <Box
            sx={{
              marginTop: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                padding: "0.5rem",
                height: "4rem",
                width: "14rem",
                borderRadius: "0.5rem",
                border: "1px solid",
                borderColor:
                  defaultStoredTheme.card.borderColor[userTheme.palette.mode],
                background:
                  defaultStoredTheme.card.backgroundColor[
                    userTheme.palette.mode
                  ],
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {theme.wideLogo[mode] ? (
                <img
                  src={theme.wideLogo[mode]}
                  alt="wideLogo"
                  crossOrigin="anonymous"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </Box>

            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="wideLogoInput"
              onChange={(e) => handleLogoUpload(e, "wideLogo")}
            />

            <label htmlFor="wideLogoInput">
              <Button
                variant="contained"
                sx={{ borderRadius: "10rem" }}
                component="span"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <UploadIcon />
                    <Typography sx={{ marginInlineStart: "0.5rem" }}>
                      {t("uiCustomization.upload")}
                    </Typography>
                  </>
                )}
              </Button>
            </label>
          </Box>
        </Grid>
      </Grid>
      {showTabBrandingFields && (
        <Grid container spacing={4} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-4)"
                  : "var(--color-34)",
            }}
          >
            {t("uiCustomization.tabIcon")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 400,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-31)"
                  : "var(--color-32)",
            }}
          >
            {t("uiCustomization.tabIconDesc")}
          </Typography>
          <Box
            sx={{
              marginTop: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                padding: "0.5rem",
                height: "4rem",
                width: "4rem",
                borderRadius: "0.5rem",
                border: "1px solid",
                borderColor:
                  defaultStoredTheme.card.borderColor[userTheme.palette.mode],
                background:
                  defaultStoredTheme.card.backgroundColor[
                    userTheme.palette.mode
                  ],
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {theme.favicon?.[mode] ? (
                <img
                  src={theme.favicon[mode]}
                  alt={t("uiCustomization.tabIcon")}
                  crossOrigin="anonymous"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </Box>

            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              style={{ display: "none" }}
              id="faviconInput"
              onChange={(e) => handleLogoUpload(e, "favicon")}
            />

            <label htmlFor="faviconInput">
              <Button
                variant="contained"
                sx={{ borderRadius: "10rem" }}
                component="span"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <UploadIcon />
                    <Typography sx={{ marginInlineStart: "0.5rem" }}>
                      {t("uiCustomization.upload")}
                    </Typography>
                  </>
                )}
              </Button>
            </label>
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-4)"
                  : "var(--color-34)",
            }}
          >
            {t("uiCustomization.tabName")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 400,
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-31)"
                  : "var(--color-32)",
            }}
          >
            {t("uiCustomization.tabNameDesc")}
          </Typography>
          <TextField
            label={t("uiCustomization.tabName")}
            value={theme.tabName || ""}
            onChange={(e) => updateThemeField(["tabName"], e.target.value)}
            fullWidth
            disabled={actionLoading}
            margin="normal"
            inputProps={{ maxLength: 50 }}
            helperText={`${(theme.tabName || "").length}/50`}
          />
        </Grid>
        </Grid>
      )}
      <Grid container spacing={4}>
        <Grid item xs={4} md={4}>
          <Box
            sx={{
              borderRadius: "1rem",
              padding: "1rem",
              border: "1px solid",
              borderColor:
                defaultStoredTheme.card.borderColor[userTheme.palette.mode],
              background:
                defaultStoredTheme.card.backgroundColor[userTheme.palette.mode],
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
              color:
                userTheme.palette.mode === "dark"
                  ? "var(--color-4)"
                  : "var(--color-34)",
            }}
          >
            {mode === "light" ? (
              <Typography>{t("theme.lightMode")}</Typography>
            ) : mode === "dark" ? (
              <Typography>{t("theme.darkMode")}</Typography>
            ) : (
              ""
            )}
            <MaterialUISwitch
              checked={mode === "dark"}
              onChange={toggleTheme}
              sx={{ marginInlineEnd: 1 }}
            />
          </Box>
          <Box
            sx={{
              maxHeight: "43rem",
              overflow: "auto",
              paddingInlineEnd: "0.5rem",
            }}
          >
            {renderThemeSection(
              theme.general,
              ["general"],
              t("uiCustomization.formBuilder.general"),
            )}
            {renderThemeSection(
              theme.header,
              ["header"],
              t("uiCustomization.formBuilder.header"),
            )}
            {renderThemeSection(
              theme.sidebar,
              ["sidebar"],
              t("uiCustomization.formBuilder.sidebar"),
            )}
            {renderThemeSection(
              theme.button.primary,
              ["button", "primary"],
              t("uiCustomization.formBuilder.primaryButton"),
            )}
            {renderThemeSection(
              theme.button.secondary,
              ["button", "secondary"],
              t("uiCustomization.formBuilder.secondaryButton"),
            )}
            {renderThemeSection(
              theme.button.success,
              ["button", "success"],
              t("uiCustomization.formBuilder.successButton"),
            )}
            {renderThemeSection(
              theme.button.error,
              ["button", "error"],
              t("uiCustomization.formBuilder.errorButton"),
            )}
            {renderThemeSection(
              theme.input,
              ["input"],
              t("uiCustomization.formBuilder.input"),
            )}
            {renderThemeSection(
              theme.slider,
              ["slider"],
              t("uiCustomization.formBuilder.slider"),
            )}
            {renderThemeSection(
              theme.radiobutton,
              ["radiobutton"],
              t("uiCustomization.formBuilder.radiobutton"),
            )}
            {renderThemeSection(
              theme.checkBox,
              ["checkBox"],
              t("uiCustomization.formBuilder.checkbox"),
            )}
            {renderThemeSection(
              theme.inputSwitch,
              ["inputSwitch"],
              t("uiCustomization.formBuilder.switch"),
            )}
            {renderThemeSection(
              theme.card,
              ["card"],
              t("uiCustomization.formBuilder.card"),
            )}
            {renderThemeSection(
              theme.playground,
              ["playground"],
              t("uiCustomization.formBuilder.playground"),
            )}
          </Box>
        </Grid>
        <Grid item xs={8} md={8}>
          <SubLayout theme={theme} mode={mode}></SubLayout>
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          sx={{ borderRadius: "10rem" }}
          disabled={actionLoading}
          onClick={resetTheme}
        >
          {t("uiCustomization.resetToDefault")}
        </Button>
        <Button
          variant="contained"
          sx={{ borderRadius: "10rem" }}
          onClick={() => saveTheme()}
          disabled={
            actionLoading || !hasChanges || hasInvalidFields(theme, mode)
          }
        >
          {t("uiCustomization.saveTheme")}
        </Button>
      </Box>
    </Box>
  );
};

export default ThemeComp;
